/**
 * Parse a git remote URL into a human display label: the forge (github/gitea/…)
 * and the `owner/repo` path. Pure string work — browser-safe.
 *
 * Codebases registered from SSH-alias remotes (e.g. `git@github-webemo-aaron:owner/repo`,
 * a per-account SSH host alias) were stored with the whole URL as their name. This turns
 * any of the common remote forms — scp (`[user@]host:owner/repo`), proto
 * (`scheme://host[:port]/owner/repo`), or bare shorthand — into `{ forge, ownerRepo }`
 * so the UI can render "github · owner/repo" instead of the raw URL.
 *
 * Mirrors the host/owner/repo extraction in `@maestro/core` `codebase-id.ts`
 * (canonicalRepoIdentity); kept as a separate browser-side copy because the web bundle
 * cannot import core (Node/db deps). Keep the two in sync if the forms change.
 */

export interface RepoLabel {
  /** Known forge slug derived from the host, or null for self-hosted/unknown hosts. */
  forge: string | null;
  /** `owner/repo` (or the deepest path the URL provided). */
  ownerRepo: string;
}

// scp-like: [user@]HOST:owner/repo  (HOST may be an alias such as github-webemo-aaron)
const SCP_FORM = /^[^/@]+@([^/:]+):(.+)$/;
// proto: scheme://[user@]HOST[:port]/owner/repo  (https, ssh, git, …)
const PROTO_FORM = /^[a-z][a-z0-9+.-]*:\/\/(?:[^/@]+@)?([^/:]+)(?::\d+)?\/(.+)$/i;

const FORGE_HINTS: readonly string[] = ['github', 'gitea', 'gitlab', 'bitbucket'];

function forgeFromHost(host: string | null): string | null {
  if (!host) return null;
  const h = host.toLowerCase();
  return FORGE_HINTS.find(hint => h.includes(hint)) ?? null;
}

export function formatRepoLabel(remoteUrl: string | null | undefined): RepoLabel | null {
  const trimmed = remoteUrl?.trim();
  if (!trimmed) return null;

  // Normalize away a trailing `.git` and any trailing slashes before parsing.
  const cleaned = trimmed.replace(/\.git$/i, '').replace(/\/+$/, '');

  const scp = SCP_FORM.exec(cleaned);
  if (scp) {
    return { forge: forgeFromHost(scp[1]), ownerRepo: scp[2] };
  }

  const proto = PROTO_FORM.exec(cleaned);
  if (proto) {
    return { forge: forgeFromHost(proto[1]), ownerRepo: proto[2] };
  }

  // An absolute filesystem path is a local checkout, not a remote — no owner/repo.
  if (cleaned.startsWith('/')) return null;

  // Bare shorthand (owner/repo) or any unrecognized string — show it verbatim.
  return { forge: null, ownerRepo: cleaned };
}

/**
 * Streamlined, mobile-friendly display name for a project/codebase: just the
 * repository name (the last path segment). Derived from the remote URL when
 * available, else from the stored `name` (which is often a raw remote such as
 * `git@github-mahoosuc-solutions:mahoosuc-solutions/CTOx` after a registry sync),
 * falling back to the raw name. The owner/forge belongs on a secondary line — not
 * in the primary label, which has to fit a phone screen. Project names are stored
 * in `owner/repo` form (from the git remote at registration), so taking the last
 * segment is the intended streamlining — not a human free-text title.
 *
 *   `git@github-x:owner/CTOx.git`      → `CTOx`
 *   `mahoosuc-solutions/dqm-native-iris` → `dqm-native-iris`
 *   `https://github.com/Nous/hermes-agent.git` → `hermes-agent`
 */
export function getProjectDisplayName(input: {
  name?: string | null;
  repository_url?: string | null;
}): string {
  const label = formatRepoLabel(input.repository_url) ?? formatRepoLabel(input.name);
  const ownerRepo = label?.ownerRepo ?? input.name ?? '';
  const repo = ownerRepo.split('/').filter(Boolean).pop();
  // `|| ` (not `??`) so an empty-string name also falls through to the default.
  return repo && repo.length > 0 ? repo : input.name?.trim() || 'project';
}
